import { css } from '@emotion/css';
import React, { useEffect, useMemo } from 'react';
import { formatTrustedDate, formatTrustedTime } from '../../../../shared/utils/datetime';
import { Modal } from '../../../../shared/components/modal/modal';
import Button from '../../../../shared/ui/button/button';
import { FiCalendar, FiClock, FiMapPin, FiDownload, FiX } from 'react-icons/fi';
import type { Ticket } from '../../../../api/services/tickets/types';
import { useGetTicketQrCode, useGetTicketImage } from '../../hooks';

export interface TicketDetailsModalProps extends React.HTMLAttributes<HTMLDivElement> {
  isOpen: boolean;
  onClose: () => void;
  ticket: Ticket | null;
  // Optional context to enrich display
  eventTitle?: string;
  locationName?: string;
  eventDate?: string; // ISO date
}

const header = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 'var(--spacing-md)',
  '@media (max-width: 480px)': {
    marginBottom: 'var(--spacing-sm)',
  }
});

const title = css({ 
  fontSize: 'var(--fs-500)', 
  fontWeight: 700, 
  color: 'var(--g-800)', 
  margin: 0,
  '@media (max-width: 480px)': {
    fontSize: 'var(--fs-400)',
  }
});

const meta = css({ 
  display: 'flex', 
  flexWrap: 'wrap', 
  gap: 'var(--spacing-md)', 
  color: 'var(--g-600)',
  '@media (max-width: 480px)': {
    gap: 'var(--spacing-sm)',
  }
});

const metaItem = css({ 
  display: 'flex', 
  alignItems: 'center', 
  gap: '0.4rem',
  fontSize: 'var(--fs-350)',
  '@media (max-width: 480px)': {
    fontSize: 'var(--fs-300)',
  }
});

const ticketImageWrap = css({
  marginTop: 'var(--spacing-lg)',
  display: 'flex',
  justifyContent: 'center',
  '@media (max-width: 480px)': {
    marginTop: 'var(--spacing-md)',
  }
});

const ticketImage = css({
  width: '100%',
  maxWidth: 720,
  height: 'auto',
  objectFit: 'contain',
  border: '1px solid var(--g-200)',
  borderRadius: 'var(--spacing-2xs)',
  boxShadow: 'var(--shadow-100)',
  '@media (max-width: 480px)': {
    maxWidth: '100%',
    borderRadius: 'var(--spacing-3xs)',
  }
});

const actions = css({ 
  display: 'flex', 
  gap: 'var(--spacing-sm)', 
  justifyContent: 'flex-end', 
  marginTop: 'var(--spacing-lg)',
  '@media (max-width: 480px)': {
    flexDirection: 'column',
    gap: 'var(--spacing-xs)',
    marginTop: 'var(--spacing-md)',
  }
});

function buildTicketFileName(ticket: Ticket, eventTitle?: string) {
  const slug = (eventTitle || 'ingresso')
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/(^-|-$)/g, '')
    .substring(0, 40);
  const id = ticket.code || ticket.id;
  return `${slug}-${String(id).substring(0, 8)}.jpg`;
}

export function TicketDetailsModal({ isOpen, onClose, ticket, eventTitle, locationName, eventDate, className, ...props }: TicketDetailsModalProps) {
  const { data: qrBlob, isFetching: isQrLoading } = useGetTicketQrCode(ticket?.id, isOpen && !!ticket?.id);
  const { data: imageBlob, isFetching: isImageLoading, isError: isImageError } = useGetTicketImage(ticket?.id, isOpen && !!ticket?.id);

  // Create Object URL for QR image
  const qrUrl = useMemo(() => (qrBlob ? URL.createObjectURL(qrBlob) : ''), [qrBlob]);
  useEffect(() => () => { if (qrUrl) URL.revokeObjectURL(qrUrl); }, [qrUrl]);
  // Create Object URL for Ticket image
  const ticketUrl = useMemo(() => (imageBlob ? URL.createObjectURL(imageBlob) : ''), [imageBlob]);
  useEffect(() => () => { if (ticketUrl) URL.revokeObjectURL(ticketUrl); }, [ticketUrl]);

  const dateFmt = eventDate ? formatTrustedDate(eventDate) : 'Data a definir';
  const timeFmt = eventDate ? formatTrustedTime(eventDate) : '—';

  const handleDownloadQr = () => {
    if (!qrBlob || !ticket) return;
    const url = URL.createObjectURL(qrBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ticket-qrcode-${(ticket.code || ticket.id).substring(0, 8)}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 300);
  };

  const handleDownloadTicketImage = () => {
    if (!imageBlob || !ticket) return;
    const url = URL.createObjectURL(imageBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = buildTicketFileName(ticket, eventTitle);
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 300);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className={className} {...props} title="Detalhes do Ingresso">
      {!ticket ? (
        <p>Carregando...</p>
      ) : (
        <div>
          <div className={header}>
            <h3 className={title}>{eventTitle || 'Evento'}</h3>
          </div>
          <div className={meta}>
            <div className={metaItem}><FiCalendar size={16} /><span>{dateFmt}</span></div>
            <div className={metaItem}><FiClock size={16} /><span>{timeFmt}</span></div>
            <div className={metaItem}><FiMapPin size={16} /><span>{locationName || 'Local a definir'}</span></div>
          </div>

          <div className={ticketImageWrap}>
            {!isImageLoading && !isImageError && ticketUrl && (
              <img src={ticketUrl} alt="Imagem do ingresso" className={ticketImage} />
            )}
            {isImageLoading && <span>Carregando imagem do ingresso...</span>}
            {isImageError && <span>Não foi possível carregar a imagem do ingresso.</span>}
          </div>

          <div className={actions}>
            <Button onClick={handleDownloadTicketImage} disabled={isImageLoading || !imageBlob}><FiDownload size={16} /> Baixar Ingresso Completo</Button>
            <Button onClick={handleDownloadQr} disabled={!qrBlob || isQrLoading}><FiDownload size={16} /> Baixar QR Code</Button>
            <Button onClick={onClose} style={{ background: 'var(--g-700)', borderColor: 'var(--g-700)' }}><FiX size={16} /> Fechar</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default TicketDetailsModal;
